/**
 * @file dashboard-model.ts
 * @description Чистые правила «Сводки» магазина (phase19.31, разбор A макет
 * (г); SC47, SC55): период, плитки показателей, строки «Требует внимания» и
 * «В работе», деньги без копеек.
 *
 * Только то, что уже отдаёт сервер: `GET /admin/orders/stats?days=`,
 * `GET /admin/nav-counters` (те же числа, что у меню) и число заказов «Мой
 * ход». Пустую строку не показываем — нечего делать, значит её нет; если всё
 * по нулям, `AttentionList` пишет «Всё в порядке».
 */

import type { AttentionItem } from "../ui/AttentionList";
import type { OrderStatsDto } from "../../lib/api-orders";
import type { NavCounters } from "../../lib/shop-nav";
import { pluralRu } from "../../lib/plural";
import { chatSearch } from "../chat/useChatUrlParam";

/** Периоды показателей — сервер принимает `days`. */
export const DASHBOARD_PERIODS = [7, 30] as const;
export type DashboardPeriod = (typeof DASHBOARD_PERIODS)[number];
export const DEFAULT_DASHBOARD_PERIOD: DashboardPeriod = 30;

/** `?days=` из адреса → период; мусор — 30 дней. */
export function parseDashboardPeriod(raw: string | null): DashboardPeriod {
  const value = Number(raw);
  return (DASHBOARD_PERIODS as readonly number[]).includes(value) ? (value as DashboardPeriod) : DEFAULT_DASHBOARD_PERIOD;
}

/** «7 дней», «30 дней». */
export function periodLabel(days: number): string {
  return `${days} ${pluralRu(days, ["день", "дня", "дней"])}`;
}

/** Деньги на сводке — без копеек: «12 330 ₽» (разбор A §0). */
export function formatRubShort(value: string | number | null | undefined): string {
  const num = Number(value ?? 0);
  if (!Number.isFinite(num)) return "—";
  return `${Math.round(num).toLocaleString("ru-RU", { maximumFractionDigits: 0 })} ₽`;
}

/** Неразрывный пробел: «20 %» не переносится по строкам. */
const NBSP = String.fromCharCode(0xa0);

/** Доля возвратов: «20 %», «12,5 %». */
export function formatPercent(value: number): string {
  return `${value.toLocaleString("ru-RU", { maximumFractionDigits: 1 })}${NBSP}%`;
}

/** Плитка показателя (≤ 4, SC47: без дублей вроде «GMV» рядом с «Выручкой»). */
export interface DashboardMetric {
  key: "revenue" | "orders" | "avgCheck" | "returns";
  label: string;
  value: string;
  hint: string;
}

export function dashboardMetrics(stats: OrderStatsDto, days: number): DashboardMetric[] {
  const period = `за ${periodLabel(days)}`;
  const done = stats.completedCount;
  return [
    // `gmv` — сумма завершённых заказов за выбранный период (это и есть выручка).
    { key: "revenue", label: "Выручка", value: formatRubShort(stats.gmv), hint: `завершённые заказы ${period}` },
    {
      key: "orders",
      label: "Заказы",
      value: String(stats.ordersCount),
      hint: `${done} ${pluralRu(done, ["завершён", "завершено", "завершено"])} · ${stats.cancelledCount} ${pluralRu(stats.cancelledCount, ["отменён", "отменено", "отменено"])}`,
    },
    { key: "avgCheck", label: "Средний чек", value: done > 0 ? formatRubShort(stats.avgCheck) : "—", hint: "по завершённым заказам" },
    {
      key: "returns",
      label: "Возвраты",
      value: formatPercent(stats.returnsRate),
      hint: `к ${done} ${pluralRu(done, ["завершённому", "завершённым", "завершённым"])}`,
    },
  ];
}

/** Что известно для «Требует внимания»: счётчики меню и «Мой ход». */
export interface AttentionSource {
  counters: NavCounters;
  /** Заказов, где сейчас ход менеджера; null — неизвестно (нет права, ошибка). */
  myTurn: number | null;
}

/** Строки «Требует внимания» — только ненулевые, в порядке срочности. */
export function dashboardAttention({ counters, myTurn }: AttentionSource, base: string): AttentionItem[] {
  const items: AttentionItem[] = [];
  const attention = counters.ordersAttention ?? 0;
  if (attention > 0) {
    items.push({
      key: "orders-attention",
      tone: "warning",
      title: "Доставка требует внимания",
      caption: "отправки без движения и заявки без расчёта",
      count: attention,
      to: `${base}/orders/attention`,
    });
  }
  if (myTurn && myTurn > 0) {
    items.push({
      key: "my-turn",
      tone: "info",
      title: `Ваш ход в ${myTurn} ${pluralRu(myTurn, ["заказе", "заказах", "заказах"])}`,
      to: `${base}/orders?owner=me`,
    });
  }
  const chat = counters.chatUnread ?? 0;
  if (chat > 0) {
    items.push({
      key: "chat",
      tone: "info",
      title: `${chat} ${pluralRu(chat, ["непрочитанный диалог", "непрочитанных диалога", "непрочитанных диалогов"])}`,
      // Окно «Сообщения» открывается поверх «Сводки» (отдельной страницы нет).
      to: `${base}${chatSearch({})}`,
    });
  }
  const reviews = counters.reviewsPending ?? 0;
  if (reviews > 0) {
    items.push({
      key: "reviews",
      tone: "info",
      title: `${reviews} ${pluralRu(reviews, ["отзыв ждёт", "отзыва ждут", "отзывов ждут"])} модерации`,
      to: `${base}/content/reviews`,
    });
  }
  return items;
}

/** Строки «В работе»: открытые заказы и очередь склада (по правам). */
export function dashboardWork(
  input: { openOrders: number | null; pickingQueue: number | undefined; canWms: boolean },
  base: string,
): AttentionItem[] {
  const items: AttentionItem[] = [];
  if (input.openOrders !== null) {
    items.push({ key: "open", tone: "info", title: "Открытые заказы", count: input.openOrders, to: `${base}/orders?open=1` });
  }
  if (input.canWms && input.pickingQueue !== undefined) {
    const n = input.pickingQueue;
    items.push({
      key: "picking",
      tone: n > 0 ? "warning" : "info",
      title: "Очередь склада",
      caption: n > 0 ? "новые задания на сборку ждут сборщика" : "новых заданий на сборку нет",
      count: n,
      to: `${base}/wms/picking`,
    });
  }
  return items;
}
